import React, { useMemo, useState } from "react";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Button from "@amzn/meridian/button";
import Tooltip from "@amzn/meridian/tooltip";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import infoTokens from "@amzn/meridian-tokens/base/icon/info";
import { DataKind, ShiftSummaryCategory } from "../../../pages/reviewAndPublish/model";
import { buildProcessPathData } from "../../../pages/shiftPlanning/CurrentPlanSummary/v2/processPathRows";
import { TOOLTIP_V2_PROCESS_PATH_ACTUAL_VS_PROJECTED } from "../../../i18n/tooltips";
import { getActualColors } from "../../../utils/midShiftActuals";

interface ShiftDataTableProps {
  /** Full shift summary — the "headcount" category plus the synthesized "hours"
   *  category (see processShiftSummaryData). Both render as expandable sections. */
  shiftSummary?: ShiftSummaryCategory[];
  /**
   * RCC_MID_SHIFT_REPLAN enabled: colour actual (elapsed) hour columns green (header, totals and
   * values) and show the info
   * button explaining the colours. The container has already removed actual hours when false.
   */
  showActuals?: boolean;
  /** Dark theme active (picks the readable green for actual hour headers). */
  isDarkMode?: boolean;
}

interface ProcessedRow {
  id: string;
  label: string;
  values: number[];
}

interface ProcessedSection {
  key: string;
  title: string;
  timeSlots: string[];
  dataKinds: DataKind[];
  rows: ProcessedRow[];
  totals: number[];
}

const numberFormatter = new Intl.NumberFormat("en-US");

const formatValue = (value: number): string =>
  Number.isInteger(value) ? numberFormatter.format(value) : numberFormatter.format(Number(value.toFixed(2)));

// Human-readable section titles for the known categories.
const SECTION_TITLES: Record<string, string> = {
  headcount: "Headcount Totals",
  hours: "Hours Totals",
};

/**
 * Refactored (V2) Process Path Analysis table — UTR-REFLEX-1165.
 *
 * Renders both the "headcount" and synthesized "hours" categories as expandable
 * grouped sections using Meridian's Table (native nested/expandable rows). Each
 * section's totals row is the parent; its process-path rows are nested children
 * that Meridian shows/hides via the `open` prop. Wrapped in an outline Box so the
 * surface + padding come from Meridian rather than custom CSS. Sibling of the V1
 * ShiftDataTable so V1 is untouched.
 */
const ShiftDataTable: React.FC<ShiftDataTableProps> = ({ shiftSummary, showActuals = false, isDarkMode = false }) => {
  const actualTextColor = getActualColors(isDarkMode).text;

  // Which sections are expanded (default: all expanded).
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const sections: ProcessedSection[] = useMemo(() => {
    if (!shiftSummary || shiftSummary.length === 0) {
      return [];
    }

    return shiftSummary
      .filter((category) => category.planOutput && category.planOutput.length > 0)
      .map((category) => {
        // Shared derivation (labels, per-slot values with 0-fill, descending sort
        // by first slot, column totals) — kept identical to the PDF export.
        const { timeSlots, dataKinds, rows, totals } = buildProcessPathData(category);

        return {
          key: category.heading,
          title: SECTION_TITLES[category.heading] ??
            `${category.heading.charAt(0).toUpperCase()}${category.heading.slice(1)} Totals`,
          timeSlots,
          dataKinds,
          rows: rows.map((row) => ({
            id: `${category.heading}-${row.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`,
            label: row.label,
            values: row.values,
          })),
          totals,
        };
      });
  }, [shiftSummary]);

  if (sections.length === 0) {
    return null;
  }

  // All sections share the same time-slot columns; use the first section's.
  const timeSlots = sections[0].timeSlots;
  const dataKinds = sections[0].dataKinds;

  // An actual (elapsed) hour column is green top to bottom: header, section totals and
  // process-path values. Only when the flag is on; untagged hours count as projected.
  const isActualSlot = (i: number): boolean => showActuals && dataKinds[i] === DataKind.ACTUAL;
  const renderValue = (value: number, i: number, testId: string) =>
    isActualSlot(i) ? (
      <span data-testid={testId} style={{ color: actualTextColor }}>{formatValue(value)}</span>
    ) : (
      formatValue(value)
    );

  return (
    <Box type="outline" spacingInset="400">
      <Column spacing="300">
        <Row spacing="100" alignmentVertical="center">
          <Heading type="h400" level={2}>
            Process Path Analysis
          </Heading>
          {showActuals && (
            // Meridian Tooltip manages its own hover/focus state and anchors to the icon button
            // (same pattern as the TopMetricsRow info icons).
            <Tooltip position="top" title={TOOLTIP_V2_PROCESS_PATH_ACTUAL_VS_PROJECTED}>
              <Button type="icon" size="small" label="Info: Process Path Analysis">
                <Icon tokens={infoTokens} />
              </Button>
            </Tooltip>
          )}
        </Row>

        <Table
          headerRows={1}
          headerColumns={1}
          showDividers={true}
          fixHeaderRows={false}
          spacing="small"
        >
          {/* Header row: sticky "All" label + one column per time slot */}
          <TableRow>
            <TableCell header={true}>All</TableCell>
            {timeSlots.map((slot, i) => (
              <TableCell key={slot} header={true} alignmentHorizontal="end">
                {isActualSlot(i) ? (
                  // Recolours only the label; Meridian's header cell keeps its own weight/spacing.
                  <span data-testid="actual-hour-header" style={{ color: actualTextColor }}>{slot}</span>
                ) : (
                  slot
                )}
              </TableCell>
            ))}
          </TableRow>

          {sections.map((section) => {
            const isOpen = !collapsedSections.has(section.key);
            return (
              // Group/totals row is the parent; process-path rows are nested children
              // that Meridian shows/hides via `open` (native expand/collapse — no
              // custom caret or CSS animation needed).
              <TableRow
                key={section.key}
                open={isOpen}
                onClick={() => toggleSection(section.key)}
                highlightOnHover={true}
              >
                <TableCell header={true}>{section.title}</TableCell>
                {section.totals.map((total, i) => (
                  <TableCell key={i} alignmentHorizontal="end">
                    {renderValue(total, i, "actual-hour-total")}
                  </TableCell>
                ))}

                {section.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell header={true}>
                      <Text type="b300">{row.label}</Text>
                    </TableCell>
                    {row.values.map((value, i) => (
                      <TableCell key={i} alignmentHorizontal="end">
                        {renderValue(value, i, "actual-hour-value")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableRow>
            );
          })}
        </Table>
      </Column>
    </Box>
  );
};

export default ShiftDataTable;
